import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
} from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowUp,
  ChevronRight,
  Filter,
  MessagesSquare,
  Search,
  X,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { CountryCell } from "../../components/CountryCell/CountryCell";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Input } from "../../components/Input/Input";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Select, type Option } from "../../components/Select/Select";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { ApiError, describeError } from "../../lib/errors";
import { isSearchTooShort } from "../../lib/search";
import { useProject } from "../projects/useProject";
import {
  cursorOf,
  getPlayerSummary,
  listGuildOptions,
  listStream,
  type StreamPageParams,
} from "./api";
import { ChatContextPanel } from "./ChatContextPanel";
import {
  guildOptionsQueryKey,
  playerSummaryQueryKey,
  streamHeadQueryKey,
  streamQueryKey,
} from "./queryKeys";
import {
  compareRows,
  dayKey,
  dayLabel,
  formatClock,
  formatExactTime,
  searchSnippet,
} from "./streamFormat";
import type { StreamFilters, StreamMessage, StreamResponse } from "./types";
import styles from "./ChatStreamPage.module.css";

const PAGE_SIZE = 50;
const POLL_MS = 15_000;

/** A page as the stream keeps it; the first one remembers when it was read. */
type StreamPage = StreamResponse & { readAt?: number };
type StreamData = InfiniteData<StreamPage, StreamPageParams>;

/**
 * Project → Chat: the text messages living players wrote in any guild of the
 * project, newest first. Read-only — moderation stays on the guild page.
 *
 * Only the newest page is polled. What it brings above the reader waits behind
 * the "new messages" pill; a shown row missing from its window was deleted and
 * drops out.
 */
export function ChatStreamPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: project } = useProject(id);

  const urlQ = searchParams.get("q") ?? "";
  const guild = searchParams.get("guild") ?? "";
  const player = searchParams.get("player") ?? "";
  const messageId = searchParams.get("message") ?? "";

  const [q, setQ] = useState(urlQ);
  const [debouncedQ, setDebouncedQ] = useState(urlQ.trim());
  // A `q` the URL gained or lost on its own — the sidebar link, Back — wins
  // over the box; the page's own writes arrive equal to `searchQ`.
  const [seenUrlQ, setSeenUrlQ] = useState(urlQ);
  if (urlQ !== seenUrlQ) {
    setSeenUrlQ(urlQ);
    if (urlQ !== (isSearchTooShort(debouncedQ) ? "" : debouncedQ)) {
      setQ(urlQ);
      setDebouncedQ(urlQ.trim());
    }
  }
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const searchQ = isSearchTooShort(debouncedQ) ? "" : debouncedQ;

  useEffect(() => {
    if (searchQ === urlQ) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (searchQ) next.set("q", searchQ);
        else next.delete("q");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ]);

  const filters: StreamFilters = useMemo(
    () => ({ q: searchQ, guild, player }),
    [searchQ, guild, player],
  );
  const streamKey = streamQueryKey(id, filters);

  const stream = useInfiniteQuery<
    StreamPage,
    Error,
    StreamData,
    typeof streamKey,
    StreamPageParams
  >({
    queryKey: streamKey,
    queryFn: async ({ pageParam }) => {
      const res = await listStream(id, filters, { ...pageParam, limit: PAGE_SIZE });
      return pageParam.before ? res : { ...res, readAt: Date.now() };
    },
    initialPageParam: {},
    getNextPageParam: (last): StreamPageParams | undefined =>
      last.has_more && last.items.length > 0
        ? { before: cursorOf(last.items[last.items.length - 1]) }
        : undefined,
    enabled: !!id,
  });

  const head = useQuery({
    queryKey: streamHeadQueryKey(id, filters),
    queryFn: () => listStream(id, filters, { limit: PAGE_SIZE }),
    refetchInterval: POLL_MS,
    enabled: !!id && stream.isSuccess,
  });

  // Messages the open panel found deleted, with when it found out: the row
  // leaves only on a head poll after that, so the panel shows the deletion
  // before the selection moves on.
  const [goneAt, setGoneAt] = useState<ReadonlyMap<string, number>>(
    () => new Map(),
  );

  const loaded = useMemo(() => {
    const seen = new Set<string>();
    const out: StreamMessage[] = [];
    for (const page of stream.data?.pages ?? []) {
      for (const it of page.items) {
        if (seen.has(it.id)) continue;
        seen.add(it.id);
        out.push(it);
      }
    }
    return out;
  }, [stream.data]);

  const firstReadAt = stream.data?.pages[0]?.readAt ?? Infinity;
  const headFresh = !!head.data && head.dataUpdatedAt >= firstReadAt;
  const items = useMemo(() => {
    if (!headFresh || !head.data) return loaded;
    // The head was read after the rows on screen, so everything from its
    // oldest row up is what exists now; below that nothing can be judged.
    const rows = head.data.items;
    const inHead = new Set(rows.map((it) => it.id));
    const oldest = head.data.has_more ? rows[rows.length - 1] : null;
    return loaded.filter((it) => {
      if (inHead.has(it.id)) return true;
      if (oldest && compareRows(it, oldest) < 0) return true;
      if (it.id !== messageId) return false;
      const at = goneAt.get(it.id);
      return at === undefined || head.dataUpdatedAt <= at;
    });
  }, [loaded, head.data, head.dataUpdatedAt, headFresh, goneAt, messageId]);

  const pending = useMemo(() => {
    const top = loaded[0];
    if (!head.data || !top) return [];
    const shown = new Set(loaded.map((it) => it.id));
    return head.data.items.filter(
      (it) => !shown.has(it.id) && compareRows(it, top) > 0,
    );
  }, [head.data, loaded]);
  const pendingMore =
    !!head.data?.has_more && pending.length === head.data.items.length;

  const lastPage = stream.data?.pages[stream.data.pages.length - 1];
  const historyWindow = lastPage?.history_window ?? 500;
  const total = stream.data?.pages[0]?.total;

  const summary = useQuery({
    queryKey: playerSummaryQueryKey(id, player),
    queryFn: () => getPlayerSummary(id, player),
    enabled: !!id && !!player,
  });
  const guilds = useQuery({
    queryKey: guildOptionsQueryKey(id),
    queryFn: () => listGuildOptions(id),
    enabled: !!id,
  });

  const cardRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // --- selection ------------------------------------------------------ //

  // The row the keyboard points at while the panel is closed; with the panel
  // open the URL's `message` is the selection.
  const [cursorId, setCursorId] = useState<string | null>(null);
  const selectedId = messageId || cursorId;
  const selectedIdx = selectedId
    ? items.findIndex((it) => it.id === selectedId)
    : -1;

  function updateParams(
    patch: Record<string, string | null>,
    replace = false,
  ) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace },
    );
  }

  function openPanel(rowId: string) {
    updateParams({ message: rowId }, !!messageId);
  }

  function closePanel() {
    setCursorId(messageId || null);
    updateParams({ message: null });
  }

  function select(rowId: string) {
    if (messageId) updateParams({ message: rowId }, true);
    else setCursorId(rowId);
  }

  function move(delta: number) {
    if (items.length === 0) return;
    const from = selectedIdx < 0 ? (delta > 0 ? -1 : items.length) : selectedIdx;
    const to = Math.min(items.length - 1, Math.max(0, from + delta));
    select(items[to].id);
  }

  useEffect(() => {
    if (!selectedId) return;
    document
      .querySelector(`[data-row-id="${CSS.escape(selectedId)}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [selectedId]);

  // When the open row is deleted out of the list the selection moves to
  // whichever row took its place. A different filter is a different list, so
  // a row missing from it moves nothing.
  const filterSig = `${searchQ}\n${guild}\n${player}`;
  const prevItemsRef = useRef({ sig: filterSig, rows: [] as StreamMessage[] });
  useEffect(() => {
    const prev = prevItemsRef.current;
    prevItemsRef.current = { sig: filterSig, rows: items };
    if (prev.sig !== filterSig) return;
    if (!messageId || items.some((it) => it.id === messageId)) return;
    const was = prev.rows.findIndex((it) => it.id === messageId);
    if (was < 0 || items.length === 0) return;
    updateParams({ message: items[Math.min(was, items.length - 1)].id }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, filterSig]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const typing =
        !!t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable);
      if (typing) {
        if (e.key === "Escape") t.blur();
        return;
      }
      // A focused control keeps its own keys; Esc still closes the panel.
      if (
        e.key !== "Escape" &&
        t?.closest('button, a, select, summary, [role="button"], [tabindex]')
      ) {
        return;
      }
      if (document.querySelector('[role="dialog"], [role="listbox"]')) return;
      switch (e.key) {
        case "/":
          e.preventDefault();
          searchRef.current?.focus();
          break;
        case "j":
        case "ArrowDown":
          e.preventDefault();
          move(1);
          break;
        case "k":
        case "ArrowUp":
          e.preventDefault();
          move(-1);
          break;
        case "Enter":
          if (selectedId && !messageId) {
            e.preventDefault();
            openPanel(selectedId);
          }
          break;
        case "Escape":
          if (messageId) closePanel();
          break;
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // --- filters -------------------------------------------------------- //

  const activeFilters = [searchQ, guild, player].filter(Boolean).length;

  function setGuild(value: string) {
    updateParams({ guild: value || null });
  }

  function setPlayer(value: string) {
    updateParams({ player: value || null });
  }

  function clearFilters() {
    setQ("");
    setDebouncedQ("");
    updateParams({ q: null, guild: null, player: null });
  }

  function showNew() {
    void stream.refetch().then(() => {
      void head.refetch();
      scrollToTop(cardRef.current);
    });
  }

  const guildOptions: Option[] = useMemo(() => {
    const opts: Option[] = [
      { value: "", label: "All guilds", muted: true },
      ...(guilds.data ?? []).map((g) => ({ value: g.id, label: g.name })),
    ];
    if (guild && !opts.some((o) => o.value === guild)) {
      const seen = items.find((it) => it.guild_id === guild);
      opts.push({ value: guild, label: seen?.guild_name ?? "Unknown guild" });
    }
    return opts;
  }, [guilds.data, guild, items]);

  const playerName =
    summary.data?.player.display_name ??
    items.find((it) => it.sender_id === player)?.sender_name ??
    (summary.isError ? "Unknown player" : "…");

  // --- render --------------------------------------------------------- //

  const header = (
    <PageHeader
      breadcrumb={[
        { label: "All projects", to: "/projects" },
        { label: project?.name ?? "Project", to: `/projects/${id}/overview` },
        { label: "Chat" },
      ]}
      title="Chat"
      subtitle="Messages from real players across all guilds · read-only"
    />
  );

  const toolbar = (
    <div className={styles.toolbar}>
      <div className={styles.search}>
        <Input
          ref={searchRef}
          placeholder="Search messages or players…"
          aria-label="Search messages"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          leadingIcon={<Search size={14} />}
          trailingIcon={
            q ? (
              <button
                type="button"
                className={styles.searchClear}
                aria-label="Clear search"
                onClick={() => setQ("")}
              >
                <X size={13} aria-hidden="true" />
              </button>
            ) : undefined
          }
        />
      </div>
      <Select
        value={guild}
        onChange={setGuild}
        options={guildOptions}
        aria-label="Guild"
        onClear={guild ? () => setGuild("") : undefined}
        clearLabel="Clear guild"
      />
      {player && (
        <span className={styles.fchip}>
          <span className={styles.fchipKey}>Player:</span> {playerName}
          <button
            type="button"
            className={styles.fchipX}
            aria-label="Remove filter"
            onClick={() => setPlayer("")}
          >
            <X size={12} aria-hidden="true" />
          </button>
        </span>
      )}
      {activeFilters >= 2 && (
        <Button variant="ghost" onClick={clearFilters}>
          Clear filters
        </Button>
      )}
      <span className={styles.spacer} />
      <span className={styles.live}>
        <span className={styles.liveDot} aria-hidden="true" />
        Live · updates every 15s
      </span>
    </div>
  );

  let resultLine = null;
  if (player && summary.data) {
    const s = summary.data;
    resultLine = (
      <div className={`${styles.resultLine} ${styles.resultWho}`}>
        <Avatar name={s.player.display_name} size={20} />
        <span>
          <b>{s.player.display_name}</b> · <b>{s.message_count.toLocaleString()}</b>{" "}
          {s.message_count === 1 ? "message" : "messages"} ·{" "}
          <b>{s.guild_count.toLocaleString()}</b>{" "}
          {s.guild_count === 1 ? "guild" : "guilds"} · in retained chat history ·{" "}
          <Link
            to={`/projects/${id}/players/${s.player.id}`}
            className={styles.linkInline}
          >
            View player →
          </Link>
        </span>
      </div>
    );
  } else if (searchQ && stream.isSuccess && items.length > 0) {
    const n = `${items.length.toLocaleString()}${stream.hasNextPage ? "+" : ""}`;
    resultLine = (
      <div className={styles.resultLine}>
        <b>{n}</b> {items.length === 1 && !stream.hasNextPage ? "message matches" : "messages match"}{" "}
        <code>{searchQ}</code> · in the message text and the player name ·{" "}
        <button
          type="button"
          className={styles.linkInline}
          onClick={() => setQ("")}
        >
          Clear search
        </button>
      </div>
    );
  }

  let body;
  if (stream.isPending) {
    body = <LoadingTable />;
  } else if (stream.isError) {
    body = (
      <Alert variant="danger" title="Couldn't load messages">
        {describeError(
          stream.error instanceof ApiError ? stream.error.code : "internal",
        )}
      </Alert>
    );
  } else if (items.length === 0) {
    body =
      activeFilters > 0 ? (
        <EmptyState
          icon={<Search size={24} />}
          title="No messages match these filters"
          description={`Only the retained chat history is searched — the last ${historyWindow.toLocaleString()} messages per guild.`}
          action={
            <Button variant="secondary" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <EmptyState
          icon={<MessagesSquare size={24} />}
          title="No messages from players yet"
          description="Bots, system messages and member events are not shown here."
        />
      );
  } else {
    body = (
      <div className={styles.card} ref={cardRef}>
        {pending.length > 0 && (
          <div className={styles.newPillWrap}>
            <button type="button" className={styles.newPill} onClick={showNew}>
              <ArrowUp size={13} aria-hidden="true" />
              {pending.length}
              {pendingMore ? "+" : ""} new{" "}
              {pending.length === 1 && !pendingMore ? "message" : "messages"}
            </button>
          </div>
        )}
        <table className={styles.table}>
          <TableHead />
          <tbody>
            {items.map((it, i) => (
              <Fragment key={it.id}>
                {(i === 0 || dayKey(items[i - 1].created_at) !== dayKey(it.created_at)) && (
                  <tr className={styles.dayRow}>
                    <td colSpan={5}>
                      <div className={styles.daySep}>
                        <span>{dayLabel(it.created_at)}</span>
                      </div>
                    </td>
                  </tr>
                )}
                <StreamRow
                  row={it}
                  q={searchQ}
                  selected={it.id === selectedId}
                  onOpen={() => {
                    setCursorId(it.id);
                    openPanel(it.id);
                  }}
                  onPlayer={() => setPlayer(it.sender_id)}
                  onGuild={() => setGuild(it.guild_id)}
                />
              </Fragment>
            ))}
          </tbody>
        </table>
        <div className={styles.cardFoot}>
          <span>
            Showing {items.length.toLocaleString()}
            {total !== undefined && ` of ${total.toLocaleString()}`} · newest
            first · chat keeps the last {historyWindow.toLocaleString()}{" "}
            messages per guild
          </span>
          <span className={styles.footRight}>
            <span className={styles.kbdHint}>
              <span className={styles.kbd}>/</span> search ·{" "}
              <span className={styles.kbd}>j</span>
              <span className={styles.kbd}>k</span> move ·{" "}
              <span className={styles.kbd}>Enter</span> context ·{" "}
              <span className={styles.kbd}>Esc</span> close
            </span>
            {stream.hasNextPage && (
              <Button
                variant="secondary"
                size="sm"
                loading={stream.isFetchingNextPage}
                disabled={stream.isFetchingNextPage}
                onClick={() => void stream.fetchNextPage()}
              >
                Load older
              </Button>
            )}
          </span>
        </div>
      </div>
    );
  }

  const prevRow = selectedIdx > 0 ? items[selectedIdx - 1] : null;
  const nextRow =
    selectedIdx >= 0 && selectedIdx < items.length - 1
      ? items[selectedIdx + 1]
      : null;

  return (
    <>
      {header}
      <div className={`${styles.page} ${messageId ? styles.pageWide : ""}`}>
        {toolbar}
        {resultLine}
        {messageId ? (
          <div className={styles.split}>
            <div className={styles.splitMain}>{body}</div>
            <ChatContextPanel
              key={messageId}
              projectId={id}
              messageId={messageId}
              onGone={(gone) =>
                setGoneAt((m) => {
                  if (m.has(gone)) return m;
                  const next = new Map(m);
                  next.set(gone, Date.now());
                  return next;
                })
              }
              createdAt={selectedIdx >= 0 ? items[selectedIdx].created_at : undefined}
              onClose={closePanel}
              onPrev={prevRow ? () => select(prevRow.id) : null}
              onNext={nextRow ? () => select(nextRow.id) : null}
            />
          </div>
        ) : (
          body
        )}
      </div>
    </>
  );
}

function TableHead() {
  return (
    <thead>
      <tr>
        <th style={{ width: 52 }}>Time</th>
        <th style={{ width: 160 }}>Player</th>
        <th style={{ width: 130 }}>Guild</th>
        <th>Message</th>
        <th style={{ width: 30 }} aria-label="Open" />
      </tr>
    </thead>
  );
}

function StreamRow({
  row,
  q,
  selected,
  onOpen,
  onPlayer,
  onGuild,
}: {
  row: StreamMessage;
  q: string;
  selected: boolean;
  onOpen: () => void;
  onPlayer: () => void;
  onGuild: () => void;
}) {
  const snippet = searchSnippet(row.body, q);
  const stop = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };
  return (
    <tr
      className={`${styles.msgRow} ${selected ? styles.selected : ""}`}
      data-row-id={row.id}
      aria-current={selected || undefined}
      onClick={onOpen}
    >
      <td className={styles.timeCell}>
        <span className={styles.mono}>{formatClock(row.created_at)}</span>
        <span className={styles.tip} role="tooltip">
          {formatExactTime(row.created_at)}
        </span>
      </td>
      <td>
        <div className={styles.who}>
          <Avatar name={row.sender_name} size={20} />
          <button
            type="button"
            className={`${styles.name} ${styles.fset}`}
            title={row.sender_name}
            aria-label={`Filter by player ${row.sender_name}`}
            onClick={stop(onPlayer)}
          >
            {row.sender_name}
          </button>
          <CountryCell code={row.sender_country} variant="flag" />
          <span className={styles.fsetIcon} aria-hidden="true" onClick={stop(onPlayer)}>
            <Filter size={12} />
          </span>
        </div>
      </td>
      <td>
        <div className={styles.who}>
          <button
            type="button"
            className={`${styles.guildName} ${styles.fset}`}
            title={row.guild_name}
            aria-label={`Filter by guild ${row.guild_name}`}
            onClick={stop(onGuild)}
          >
            {row.guild_name}
          </button>
          <span className={styles.fsetIcon} aria-hidden="true" onClick={stop(onGuild)}>
            <Filter size={12} />
          </span>
        </div>
      </td>
      <td className={styles.msg}>
        {snippet.clipped && "…"}
        {snippet.segments.map((s, i) =>
          s.hit ? (
            <mark key={i} className={styles.hit}>
              {s.text}
            </mark>
          ) : (
            <Fragment key={i}>{s.text}</Fragment>
          ),
        )}
      </td>
      <td className={styles.chevCell}>
        <ChevronRight size={14} aria-hidden="true" />
      </td>
    </tr>
  );
}

function LoadingTable() {
  const widths = ["72%", "88%", "64%", "80%", "56%", "76%", "68%", "84%"];
  return (
    <div className={styles.card} data-testid="chat-loading">
      <table className={styles.table}>
        <TableHead />
        <tbody>
          {widths.map((w, i) => (
            <tr key={i}>
              <td>
                <Skeleton width={34} height={11} />
              </td>
              <td>
                <div className={styles.who}>
                  <Skeleton width={20} height={20} radius={999} />
                  <Skeleton width="70%" height={11} />
                </div>
              </td>
              <td>
                <Skeleton width="80%" height={11} />
              </td>
              <td>
                <Skeleton width={w} height={11} />
              </td>
              <td>
                <Skeleton width={14} height={14} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Scrolls the nearest scrolling ancestor of `el` back to its top. */
function scrollToTop(el: HTMLElement | null) {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollTop > 0) {
      node.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    node = node.parentElement;
  }
  window.scrollTo({ top: 0, behavior: "smooth" });
}
